<script setup lang="ts">
import { computed, ref } from 'vue'
import { Head, useForm } from '@inertiajs/vue3'
import { notify } from '@kyvg/vue3-notification'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Table from '@/Components/Table/Table.vue'
import Button from '@/Components/Utils/Button.vue'
import Modal from '@/Components/Utils/Modal.vue'
import InputNumber from 'primevue/inputnumber'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { useFormatTime } from '@/Composables/useFormatTime'

interface PlanoPaper {
    id: number
    paper_type: string
    grammage: number
    full_name: string
    width: number
    length: number
    price: number
    supplier: string | null
    created_at: string
}

defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    data: {}
}>()

const formatCm = (value: number) => new Intl.NumberFormat('id-ID', { maximumFractionDigits: 2 }).format(value)

const isModalOpen = ref(false)
const editingPlano = ref<PlanoPaper | null>(null)

const form = useForm({
    paper_type: '',
    grammage: '' as number | string,
    full_name: '',
    width: 79 as number | string,
    length: 109 as number | string,
    price: 4100 as number | string,
    supplier: '',
})

const suggestedFullName = computed(() =>
    form.paper_type.trim() && form.grammage ? `${form.paper_type.trim()} ${form.grammage} gsm` : ''
)

const openCreateModal = () => {
    editingPlano.value = null
    form.reset()
    form.clearErrors()
    isModalOpen.value = true
}

const openEditModal = (plano: PlanoPaper) => {
    editingPlano.value = plano
    form.clearErrors()
    form.paper_type = plano.paper_type
    form.grammage = plano.grammage
    form.full_name = plano.full_name
    form.width = plano.width
    form.length = plano.length
    form.price = plano.price
    form.supplier = plano.supplier ?? ''
    isModalOpen.value = true
}

const submit = () => {
    const options = {
        preserveScroll: true,
        onSuccess: () => {
            isModalOpen.value = false
            notify({
                title: 'Tersimpan',
                text: editingPlano.value ? 'Kertas plano berhasil diperbarui.' : 'Kertas plano berhasil ditambahkan.',
                type: 'success',
            })
        },
    }

    const request = form.transform((data) => ({
        ...data,
        full_name: data.full_name.trim() || suggestedFullName.value,
    }))

    if (editingPlano.value) {
        request.patch(route('custom-size.planos.update', editingPlano.value.id), options)
    } else {
        request.post(route('custom-size.planos.store'), options)
    }
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #other>
            <Button type="create" label="Tambah kertas plano" @click="openCreateModal" />
        </template>
    </PageHeading>

    <Table :resource="data">
        <template #cell(full_name)="{ item }">
            <div class="font-medium">{{ item.full_name }}</div>
            <div class="text-xs text-gray-400">{{ item.paper_type }} · {{ item.grammage }} gsm</div>
        </template>

        <template #cell(size)="{ item }">
            {{ formatCm(item.width) }} × {{ formatCm(item.length) }} cm
        </template>

        <template #cell(price)="{ item }">
            {{ useFormatCurrency(item.price) }}
        </template>

        <template #cell(supplier)="{ item }">
            <span :class="item.supplier ? '' : 'italic text-gray-400'">{{ item.supplier || '-' }}</span>
        </template>


        <template #cell(created_at)="{ item }">
            {{ useFormatTime(item.created_at) }}
        </template>

        <template #cell(actions)="{ item }">
            <div class="flex gap-x-2">
                <Button size="xs" type="edit" icon="far fa-pencil" @click="openEditModal(item)" />
                <ModalConfirmationDelete
                    :routeDelete="{ name: 'custom-size.planos.delete', parameters: [item.id], method: 'delete' }"
                    preserveScroll>
                    <template #default="{ changeModel }">
                        <Button size="xs" type="delete" icon="far fa-trash-alt" @click="() => changeModel()" />
                    </template>
                </ModalConfirmationDelete>
            </div>
        </template>
    </Table>

    <Modal :isOpen="isModalOpen" width="w-full max-w-2xl" @onClose="isModalOpen = false">
        <h3 class="mb-4 text-lg font-semibold text-gray-700">
            {{ editingPlano ? 'Ubah kertas plano' : 'Tambah kertas plano' }}
        </h3>

        <div class="grid gap-4 md:grid-cols-2">
            <div>
                <label class="mb-1 block text-sm text-gray-600">Jenis kertas <span class="text-red-500">*</span></label>
                <input v-model="form.paper_type" placeholder="Art Paper" class="w-full rounded-md border-gray-300 text-sm"
                    :class="form.errors.paper_type ? 'errorShake' : ''" @input="form.clearErrors('paper_type')" />
                <div v-if="form.errors.paper_type" class="mt-1 text-xs text-red-500">{{ form.errors.paper_type }}</div>
            </div>

            <div>
                <label class="mb-1 block text-sm text-gray-600">Gramatur <span class="text-red-500">*</span></label>
                <InputNumber showButtons :step="10" v-model="form.grammage" :min="1" :useGrouping="false" suffix=" gsm" placeholder="260 gsm"
                    fluid :class="form.errors.grammage ? 'errorShake' : ''" @input="form.clearErrors('grammage')" />
                <div v-if="form.errors.grammage" class="mt-1 text-xs text-red-500">{{ form.errors.grammage }}</div>
            </div>

            <div class="md:col-span-2">
                <label class="mb-1 block text-sm text-gray-600">Jenis kertas plano lengkap</label>
                <input v-model="form.full_name" :placeholder="suggestedFullName || 'Art Paper 260 gsm'"
                    class="w-full rounded-md border-gray-300 text-sm" />
                <div class="mt-0.5 text-xs text-gray-400">Kosongkan untuk memakai "{{ suggestedFullName || 'jenis kertas + gramatur' }}".</div>
            </div>

            <div>
                <label class="mb-1 flex items-center gap-x-1.5 text-sm text-gray-600">
                    <span class="inline-block size-2.5 rounded-sm border border-pink-600 bg-pink-300" />
                    Ukuran (lebar × panjang) <span class="text-red-500">*</span>
                </label>
                <div class="flex items-center gap-x-2">
                    <InputNumber showButtons :step="0.5" v-model="form.width" :min="0" :maxFractionDigits="2" locale="id-ID" fluid
                        :class="form.errors.width ? 'errorShake' : ''" inputClass="!border-pink-400 !bg-pink-100"
                        @input="form.clearErrors('width')" />
                    <span class="text-gray-400">×</span>
                    <InputNumber showButtons :step="0.5" v-model="form.length" :min="0" :maxFractionDigits="2" locale="id-ID" fluid
                        :class="form.errors.length ? 'errorShake' : ''" inputClass="!border-pink-400 !bg-pink-100"
                        @input="form.clearErrors('length')" />
                    <span class="text-xs text-gray-400">cm</span>
                </div>
                <div v-if="form.errors.width || form.errors.length" class="mt-1 text-xs text-red-500">
                    {{ form.errors.width || form.errors.length }}
                </div>
            </div>

            <div>
                <label class="mb-1 block text-sm text-gray-600">Harga per plano <span class="text-red-500">*</span></label>
                <InputNumber showButtons :step="100" v-model="form.price" mode="currency" currency="IDR" locale="id-ID" :maxFractionDigits="0"
                    :min="0" fluid :class="form.errors.price ? 'errorShake' : ''" @input="form.clearErrors('price')" />
                <div v-if="form.errors.price" class="mt-1 text-xs text-red-500">{{ form.errors.price }}</div>
            </div>

            <div class="md:col-span-2">
                <label class="mb-1 block text-sm text-gray-600">Supplier</label>
                <input v-model="form.supplier" placeholder="Nama / tempat supplier"
                    class="w-full rounded-md border-gray-300 text-sm" />
            </div>
        </div>

        <div class="mt-6 flex justify-end gap-x-2">
            <Button type="tertiary" label="Batal" @click="isModalOpen = false" />
            <Button type="save" :loading="form.processing" label="Simpan" @click="submit" />
        </div>
    </Modal>
</template>
